import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, ShoppingBag, Shirt, Trophy, Users, Check, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { GrasrotandelenBanner } from '../components/GrasrotandelenBanner';
import { useTranslation } from 'react-i18next';
import { SEO } from '../components/SEO';
import { getstoreCategories } from '../data/storeData';

import i18n from '../i18n';
const t = (k, o) => i18n.t(k, o);

export const Store = () => {
  const { t } = useTranslation();

  const clubCollectionItems = getstoreCategories().map((c) => ({
    title: t(c.titleKey, c.titleFallback),
    description: t(c.descKey, c.descFallback),
    icon: c.icon,
    id: c.id,
  }));

  return (
    <div data-testid="store-page">
      <SEO 
        title={t('pg.Store.796e84')} 
        description={t('pg.Store.20d39d')}
      />
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=1920&q=80"
            alt={t('pg.Store.4e09c3')}
            loading="eager"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/80 to-transparent" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <p className="text-green-400 font-medium uppercase tracking-widest mb-4">{t('store.subtitle', 'Klubbkolleksjon')}</p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter text-white mb-6">
            {t('store.heroTitle1', 'VIS DIN')}<br />
            <span className="text-green-400">{t('store.heroTitle2', 'KLUBBSTOLTHET')}</span>
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mb-8">
            {t('pg.Store.5af581')}
          </p>
          <a 
            href="https://cometsport.no/products/index/185?type=category"
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t('pg.Store.7b5752')}
          >
            <Button className="bg-green-500 text-white font-bold uppercase tracking-wider px-8 py-6 hover:bg-green-600">
              <ShoppingBag className="mr-2 w-5 h-5" />
              {t('pg.Store.749e1f')}
              <ExternalLink className="ml-2 w-5 h-5" />
            </Button>
          </a>
        </div>
      </section>

      {/* Comet Sport Partner Banner */}
      <section className="py-12 bg-gradient-to-r from-green-500 to-green-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="text-center lg:text-left">
              <p className="text-white/70 font-medium uppercase tracking-wider mb-2">{t('pg.Store.d328d9')}</p>
              <h2 className="text-4xl font-bold text-white mb-4">{t('pg.Store.d7bc0c')}</h2>
              <p className="text-white/90 text-lg max-w-xl">
                {t('pg.Store.8a33bc')}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href="https://cometsport.no/products/index/185?type=category"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="comet-sport-link"
              >
                <Button className="bg-white text-green-600 font-bold uppercase tracking-wider px-8 py-6 hover:bg-white/90">
                  {t('pg.Store.f0acc4')}
                  <ExternalLink className="ml-2 w-5 h-5" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* What's Available */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-green-500 font-medium uppercase tracking-widest mb-4">{t('pg.Store.b19fd9')}</p>
            <h2 className="text-4xl font-bold tracking-tighter text-slate-900 dark:text-white">
              {t('pg.Store.4298e7')}
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {clubCollectionItems.map((item, index) => (
              <div 
                key={index}
                className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 text-center hover:border-green-500/50 transition-all duration-300"
              >
                <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-8 h-8 text-green-500" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">{item.title}</h3>
                <p className="text-slate-500 dark:text-slate-400">{item.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <a 
              href="https://cometsport.no/products/index/185?type=category"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="bg-green-500 text-white font-bold uppercase tracking-wider px-8 py-6 hover:bg-green-600">
                {t('pg.Store.8b61a6')}
                <ExternalLink className="ml-2 w-5 h-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Overtrekkstøy Products Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50 dark:bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <p className="text-green-500 font-medium uppercase tracking-widest mb-4">{t('pg.Store.5d2a5d')}</p>
            <h2 className="text-4xl font-bold tracking-tighter text-slate-900 dark:text-white mb-4">
              {t('pg.Store.d541e9')}
            </h2>
            <p className="text-slate-500 dark:text-slate-400 max-w-2xl mx-auto">
              {t('pg.Store.76b10b')}
            </p>
          </div>

          {/* Important Notice */}
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-8 max-w-3xl mx-auto">
            <p className="text-yellow-600 dark:text-yellow-400 text-center font-medium">
              ⚠️ <strong>{t('pg.Store.500718')}</strong> {t('pg.Store.8a2c84')}{' '}
              <a href="https://cometsport.no/products/index/185?type=category" target="_blank" rel="noopener noreferrer" className="underline hover:text-yellow-500">
                {t('pg.Store.5edd83')}
              </a>.
            </p>
          </div>

          {/* Product Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Product 1: Half Zip Jr */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden hover:shadow-lg transition-all duration-300 group">
              <div className="aspect-square relative overflow-hidden bg-slate-100 dark:bg-slate-700">
                <img
                  src="https://customer-assets.emergentagent.com/job_veitvet-sports/artifacts/7veq6wyf_Umbro%20Ux%20Pro%20Half%20Zip%20Jr%20%28Barn%29.png"
                  alt={t('pg.Store.39958d')}
                  className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-300"
                />
                <span className="absolute top-3 left-3 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded">{t('pg.Store.a4070a')}</span>
              </div>
              <div className="p-4">
                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">{t('pg.Store.39958d')}</h3>
                <div className="flex flex-col gap-1">
                  <p className="text-slate-400 text-sm line-through">{t('pg.Store.a68cae')}</p>
                  <p className="text-green-500 font-bold text-lg">{t('pg.Store.6f81dc')}</p>
                </div>
                <p className="text-green-600 dark:text-green-400 text-xs mt-2 font-medium">{t('pg.Store.6ae05c')}</p>
              </div>
            </div>

            {/* Product 2: Training Jacket Jr */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden hover:shadow-lg transition-all duration-300 group">
              <div className="aspect-square relative overflow-hidden bg-slate-100 dark:bg-slate-700">
                <img
                  src="https://customer-assets.emergentagent.com/job_veitvet-sports/artifacts/t5ucvpez_Umbro%20Ux%20Pro%20Trn%20Jkt%20Jr_Barn.png"
                  alt={t('pg.Store.f211c9')}
                  className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-300"
                />
                <span className="absolute top-3 left-3 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded">{t('pg.Store.a4070a')}</span>
              </div>
              <div className="p-4">
                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">{t('pg.Store.f211c9')}</h3>
                <div className="flex flex-col gap-1">
                  <p className="text-slate-400 text-sm line-through">{t('pg.Store.5e1ffe')}</p>
                  <p className="text-green-500 font-bold text-lg">{t('pg.Store.3a2ab1')}</p>
                </div>
                <p className="text-green-600 dark:text-green-400 text-xs mt-2 font-medium">{t('pg.Store.6ae05c')}</p>
              </div>
            </div>

            {/* Product 3: Regular Pant Jr */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden hover:shadow-lg transition-all duration-300 group">
              <div className="aspect-square relative overflow-hidden bg-slate-100 dark:bg-slate-700">
                <img
                  src="https://customer-assets.emergentagent.com/job_veitvet-sports/artifacts/yywga2wu_Umbro%20Ux%20Pro%20Reg%20Pant%20Jr_Barn.png"
                  alt={t('pg.Store.3ea48a')}
                  className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-300"
                />
                <span className="absolute top-3 left-3 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded">{t('pg.Store.a4070a')}</span>
              </div>
              <div className="p-4">
                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">{t('pg.Store.3ea48a')}</h3>
                <div className="flex flex-col gap-1">
                  <p className="text-slate-400 text-sm line-through">{t('pg.Store.0ece56')}</p>
                  <p className="text-green-500 font-bold text-lg">{t('pg.Store.d315d4')}</p>
                </div>
                <p className="text-green-600 dark:text-green-400 text-xs mt-2 font-medium">{t('pg.Store.6ae05c')}</p>
              </div>
            </div>

            {/* Product 4: Slim Pant Jr */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden hover:shadow-lg transition-all duration-300 group">
              <div className="aspect-square relative overflow-hidden bg-slate-100 dark:bg-slate-700">
                <img
                  src="https://customer-assets.emergentagent.com/job_veitvet-sports/artifacts/zmvzmb4j_Umbro%20Ux%20Pro%20Slim%20Pant%20Jr_Barn.png"
                  alt={t('pg.Store.96d619')}
                  className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-300"
                />
                <span className="absolute top-3 left-3 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded">{t('pg.Store.a4070a')}</span>
              </div>
              <div className="p-4">
                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">{t('pg.Store.96d619')}</h3>
                <div className="flex flex-col gap-1">
                  <p className="text-slate-400 text-sm line-through">{t('pg.Store.f059ed')}</p>
                  <p className="text-green-500 font-bold text-lg">{t('pg.Store.91b646')}</p>
                </div>
                <p className="text-green-600 dark:text-green-400 text-xs mt-2 font-medium">{t('pg.Store.6ae05c')}</p>
              </div>
            </div>
          </div>

          {/* Voksen Section Header */}
          <div className="text-center mt-16 mb-8">
            <h2 className="text-4xl font-bold tracking-tighter text-slate-900 dark:text-white mb-4">
              {t('pg.Store.4fd19b')}
            </h2>
            <p className="text-slate-500 dark:text-slate-400 max-w-2xl mx-auto">
              {t('pg.Store.268f15')}
            </p>
          </div>

          {/* Voksen Product Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Voksen Product 1: Half Zip */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden hover:shadow-lg transition-all duration-300 group">
              <div className="aspect-square relative overflow-hidden bg-slate-100 dark:bg-slate-700">
                <img
                  src="https://customer-assets.emergentagent.com/job_veitvet-sports/artifacts/h38ns66w_Umbro%20Ux%20Pro%20Half%20Zip_Voksen.png"
                  alt={t('pg.Store.84d794')}
                  className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-300"
                />
                <span className="absolute top-3 left-3 bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded">{t('pg.Store.aaa3e4')}</span>
              </div>
              <div className="p-4">
                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">{t('pg.Store.84d794')}</h3>
                <div className="flex flex-col gap-1">
                  <p className="text-slate-400 text-sm line-through">{t('pg.Store.119079')}</p>
                  <p className="text-green-500 font-bold text-lg">{t('pg.Store.69aa38')}</p>
                </div>
                <p className="text-green-600 dark:text-green-400 text-xs mt-2 font-medium">{t('pg.Store.6ae05c')}</p>
              </div>
            </div>

            {/* Voksen Product 2: Training Jacket */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden hover:shadow-lg transition-all duration-300 group">
              <div className="aspect-square relative overflow-hidden bg-slate-100 dark:bg-slate-700">
                <img
                  src="https://customer-assets.emergentagent.com/job_veitvet-sports/artifacts/19neo9zp_Umbro%20Ux%20Pro%20Trn%20Jkt_Voksen.png"
                  alt={t('pg.Store.37c027')}
                  className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-300"
                />
                <span className="absolute top-3 left-3 bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded">{t('pg.Store.aaa3e4')}</span>
              </div>
              <div className="p-4">
                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">{t('pg.Store.37c027')}</h3>
                <div className="flex flex-col gap-1">
                  <p className="text-slate-400 text-sm line-through">{t('pg.Store.2b6afb')}</p>
                  <p className="text-green-500 font-bold text-lg">{t('pg.Store.b25614')}</p>
                </div>
                <p className="text-green-600 dark:text-green-400 text-xs mt-2 font-medium">{t('pg.Store.6ae05c')}</p>
              </div>
            </div>

            {/* Voksen Product 3: Regular Pant */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden hover:shadow-lg transition-all duration-300 group">
              <div className="aspect-square relative overflow-hidden bg-slate-100 dark:bg-slate-700">
                <img
                  src="https://customer-assets.emergentagent.com/job_veitvet-sports/artifacts/vslxqz2v_Umbro%20Ux%20Pro%20Reg%20Pant_Voksen.png"
                  alt={t('pg.Store.3a2fa8')}
                  className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-300"
                />
                <span className="absolute top-3 left-3 bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded">{t('pg.Store.aaa3e4')}</span>
              </div>
              <div className="p-4">
                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">{t('pg.Store.3a2fa8')}</h3>
                <div className="flex flex-col gap-1">
                  <p className="text-slate-400 text-sm line-through">{t('pg.Store.f059ed')}</p>
                  <p className="text-green-500 font-bold text-lg">{t('pg.Store.91b646')}</p>
                </div>
                <p className="text-green-600 dark:text-green-400 text-xs mt-2 font-medium">{t('pg.Store.6ae05c')}</p>
              </div>
            </div>

            {/* Voksen Product 4: Slim Pant */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden hover:shadow-lg transition-all duration-300 group">
              <div className="aspect-square relative overflow-hidden bg-slate-100 dark:bg-slate-700">
                <img
                  src="https://customer-assets.emergentagent.com/job_veitvet-sports/artifacts/1we6e903_Umbro%20Ux%20Pro%20Slim%20Pant_Voksen.png"
                  alt={t('pg.Store.61af80')}
                  className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-300"
                />
                <span className="absolute top-3 left-3 bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded">{t('pg.Store.aaa3e4')}</span>
              </div>
              <div className="p-4">
                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">{t('pg.Store.61af80')}</h3>
                <div className="flex flex-col gap-1">
                  <p className="text-slate-400 text-sm line-through">{t('pg.Store.e75b8f')}</p>
                  <p className="text-green-500 font-bold text-lg">{t('pg.Store.49df29')}</p>
                </div>
                <p className="text-green-600 dark:text-green-400 text-xs mt-2 font-medium">{t('pg.Store.6ae05c')}</p>
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <div className="mt-10 text-center">
            <a 
              href="https://cometsport.no/products/index/185?type=category"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="bg-green-500 text-white font-bold uppercase tracking-wider px-8 py-6 hover:bg-green-600">
                {t('pg.Store.b1a6b8')}
                <ExternalLink className="ml-2 w-5 h-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* How to Order */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-800">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-green-500 font-medium uppercase tracking-widest mb-4">{t('pg.Store.c73ffc')}</p>
            <h2 className="text-4xl font-bold tracking-tighter text-slate-900 dark:text-white">
              {t('pg.Store.cbcbac')}
            </h2>
          </div>

          <div className="space-y-6">
            <div className="flex items-start gap-6 bg-slate-100 dark:bg-slate-700/50 rounded-xl p-6 border border-slate-200 dark:border-slate-600">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-xl">1</span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">{t('pg.Store.422e4c')}</h3>
                <p className="text-slate-600 dark:text-slate-300 mb-3">
                  {t('pg.Store.417599')}
                </p>
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                  <p className="text-green-600 dark:text-green-400 text-sm font-medium flex items-center gap-2">
                    <span className="text-lg">💰</span>
                    <span><strong>{t('pg.Store.500718')}</strong> {t('pg.Store.9d2343')} <strong>{t('pg.Store.d726f9')}</strong> {t('pg.Store.f77026')}</span>
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-6 bg-slate-100 dark:bg-slate-700/50 rounded-xl p-6 border border-slate-200 dark:border-slate-600">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-xl">2</span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">{t('pg.Store.572596')}</h3>
                <p className="text-slate-600 dark:text-slate-300">
                  {t('pg.Store.48421b')}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-6 bg-slate-100 dark:bg-slate-700/50 rounded-xl p-6 border border-slate-200 dark:border-slate-600">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-xl">3</span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">{t('pg.Store.b7e0a6')}</h3>
                <p className="text-slate-600 dark:text-slate-300">
                  {t('pg.Store.fa74bf')}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-8 bg-green-500/10 border border-green-500/30 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <Check className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
              <div>
                <h4 className="text-slate-900 dark:text-white font-bold mb-1">{t('pg.Store.60e37e')}</h4>
                <p className="text-slate-600 dark:text-slate-300 text-sm">
                  {t('pg.Store.5159cd')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Membership Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl border border-slate-700 overflow-hidden">
            <div className="grid lg:grid-cols-2 gap-0">
              <div className="p-8 lg:p-12">
                <p className="text-green-400 font-medium uppercase tracking-widest mb-4">{t('pg.Store.47a847')}</p>
                <h2 className="text-4xl font-bold tracking-tighter text-white mb-6">
                  {t('pg.Store.633524')}
                </h2>
                <p className="text-slate-300 text-lg mb-8">
                  {t('pg.Store.6c7985')}
                </p>
                
                <div className="space-y-3 mb-8">
                  <div className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-green-400" />
                    <span className="text-white">{t('pg.Store.898b98')}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-green-400" />
                    <span className="text-white">{t('pg.Store.4f4973')}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-green-400" />
                    <span className="text-white">{t('pg.Store.6bf94e')}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-green-400" />
                    <span className="text-white">{t('pg.Store.b72985')}</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-4">
                  <a 
                    href="https://app.hoopit.io/join/veitvet-sportsklubb-532465/open?skipWelcome=true&push=true"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="store-join-btn"
                  >
                    <Button className="bg-green-500 text-white font-bold uppercase tracking-wider px-8 py-6 hover:bg-green-600">
                      {t('pg.Store.b52509')}
                      <ExternalLink className="ml-2 w-5 h-5" />
                    </Button>
                  </a>
                  <Link to="/pricing">
                    <Button variant="outline" className="border-white/30 text-white uppercase tracking-wider px-8 py-6 hover:bg-white/10">
                      {t('pg.Store.c33855')}
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                  </Link>
                </div>
              </div>
              <div className="relative hidden lg:block">
                <img 
                  src="https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=800&q=80"
                  alt={t('pg.Store.6ce148')}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-slate-900 to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact for Questions */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-800">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4">{t('pg.Store.68fde0')}</h2>
          <p className="text-slate-600 dark:text-slate-300 mb-8">
            {t('pg.Store.b45ee5')}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button variant="outline" className="border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-slate-700">
                {t('pg.Store.8d1c6b')}
              </Button>
            </Link>
            <a 
              href="https://cometsport.no/products/index/185?type=category"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="bg-green-500 text-white font-bold hover:bg-green-600">
                {t('pg.Store.749e1f')}
                <ExternalLink className="ml-2 w-4 h-4" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Grasrotandelen */}
      <GrasrotandelenBanner />
    </div>
  );
};

export default Store;
